/**
 * Audit Log presentation helpers — PURE, unit-testable. Build the compact
 * one-line summary shown in the table cell and the pretty-printed JSON shown in
 * the expandable row. The server owns every field; this only shapes display.
 */
import type { AuditEvent } from '@/lib/schemas/audit'

/**
 * Compact summary for the table cell, in precedence order:
 *  1. old → new  (when either value is present — e.g. a settings change)
 *  2. the detail object as a single-line JSON string
 *  3. the error message (for failed events with no detail)
 *  4. an em-dash when there is nothing to show
 */
export function auditSummary(event: AuditEvent): string {
  if (event.old_value != null || event.new_value != null) {
    const from = event.old_value ?? '—'
    const to = event.new_value ?? '—'
    return `${from} → ${to}`
  }
  if (event.detail != null && Object.keys(event.detail).length > 0) {
    return JSON.stringify(event.detail)
  }
  if (event.error_message != null && event.error_message !== '') {
    return event.error_message
  }
  return '—'
}

/** Pretty-printed detail JSON for the expandable row, or null when absent. */
export function formatDetailJson(detail: Record<string, unknown> | null): string | null {
  if (detail == null || Object.keys(detail).length === 0) return null
  return JSON.stringify(detail, null, 2)
}
